import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useUserVFS, type UserFile } from '@os/hooks/useUserVFS';
import { useToast } from '@os/hooks/use-toast';
import { Button } from '@os/components/ui/button';
import { Input } from '@os/components/ui/input';
import { ScrollArea } from '@os/components/ui/scroll-area';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@os/components/ui/context-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@os/components/ui/alert-dialog';
import {
  File,
  Folder,
  FolderOpen,
  Plus,
  Edit3,
  Trash2,
  Copy,
  Scissors,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  FileText,
  Image,
  Code2,
  Music,
  Video,
  Archive,
  FileCode,
  Database,
} from 'lucide-react';

interface FileTreeItem extends UserFile {
  isExpanded?: boolean;
  children?: FileTreeItem[];
  level: number;
}

interface FileTreeExplorerProps {
  currentProject: any;
  currentPath: string;
  onFileSelect: (file: UserFile) => void;
  onNavigate: (path: string) => void;
  onRefresh: () => void;
}

const getFileIcon = (fileName: string, isDirectory: boolean) => {
  if (isDirectory) return Folder;
  
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return FileCode;
    case 'css':
    case 'scss':
    case 'sass':
    case 'less':
      return Code2;
    case 'html':
    case 'htm':
      return FileCode;
    case 'json':
    case 'xml':
      return Database;
    case 'md':
    case 'txt':
      return FileText;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'svg':
    case 'webp':
      return Image;
    case 'mp3':
    case 'wav':
    case 'ogg':
      return Music;
    case 'mp4':
    case 'avi':
    case 'mov':
      return Video;
    case 'zip':
    case 'rar':
    case '7z':
      return Archive;
    default:
      return File;
  }
};

const FileTreeExplorer: React.FC<FileTreeExplorerProps> = ({
  currentProject,
  currentPath,
  onFileSelect,
  onNavigate,
  onRefresh,
}) => {
  const { files, createFile, deleteFile, listFiles, updateFile } = useUserVFS();
  const { toast } = useToast();
  
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<UserFile | null>(null);
  const [draggedItem, setDraggedItem] = useState<UserFile | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  
  const editInputRef = useRef<HTMLInputElement>(null);

  const buildFileTree = useCallback((files: UserFile[], basePath: string, level = 0): FileTreeItem[] => {
    const tree: FileTreeItem[] = [];
    const folders: FileTreeItem[] = [];
    const regularFiles: FileTreeItem[] = [];

    files.forEach(file => {
      if (file.parent_path === basePath) {
        const item: FileTreeItem = {
          ...file,
          level,
          isExpanded: expandedFolders.has(file.file_path),
          children: file.is_directory 
            ? buildFileTree(files, file.file_path, level + 1)
            : undefined,
        };

        if (file.is_directory) {
          folders.push(item);
        } else {
          regularFiles.push(item);
        }
      }
    });

    // Sort folders first, then files
    folders.sort((a, b) => a.file_name.localeCompare(b.file_name));
    regularFiles.sort((a, b) => a.file_name.localeCompare(b.file_name));

    return [...folders, ...regularFiles];
  }, [expandedFolders]);

  const toggleFolder = (folderPath: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const handleItemClick = (item: FileTreeItem) => {
    setSelectedItem(item.file_path);
    
    if (item.is_directory) {
      toggleFolder(item.file_path);
      onNavigate(item.file_path);
    } else {
      onFileSelect(item);
    }
  };

  const handleItemDoubleClick = (item: FileTreeItem) => {
    if (!item.is_directory) {
      onFileSelect(item);
    }
  };

  const startRename = (item: UserFile) => {
    setEditingItem(item.file_path);
    setEditingName(item.file_name);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const handleRename = async () => {
    if (!editingItem || !editingName.trim()) return;

    try {
      // Use memoized allFiles instead of calling listFiles again
      const item = allFiles.find(f => f.file_path === editingItem);
      if (!item) return;

      const parentPath = item.parent_path || '';
      const newPath = `${parentPath}${parentPath ? '\\' : ''}${editingName}`;
      
      // Create new file/directory with new name
      await createFile(newPath, item.content || '', item.is_directory, item.content_type);
      
      // Delete old file/directory
      await deleteFile(editingItem);
      
      toast({
        title: "Renamed",
        description: `Renamed to ${editingName}`,
      });
      
      onRefresh();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to rename item",
        variant: "destructive",
      });
    }
    
    setEditingItem(null);
    setEditingName('');
  };

  const cancelRename = () => {
    setEditingItem(null);
    setEditingName('');
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteFile(itemToDelete.file_path);
      
      toast({
        title: "Deleted",
        description: `${itemToDelete.file_name} has been deleted`,
      });
      
      onRefresh();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete item",
        variant: "destructive",
      });
    }
    
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const createNewFile = async (parentPath: string) => {
    const fileName = 'new-file.txt';
    const fullPath = `${parentPath}${parentPath ? '\\' : ''}${fileName}`;
    
    try {
      await createFile(fullPath, '');
      onRefresh();
      
      // Auto-start rename on new file
      setTimeout(() => startRename({ file_path: fullPath, file_name: fileName } as UserFile), 100);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create file",
        variant: "destructive",
      });
    }
  };

  const createNewFolder = async (parentPath: string) => {
    const folderName = 'new-folder';
    const fullPath = `${parentPath}${parentPath ? '\\' : ''}${folderName}`;
    
    try {
      await createFile(fullPath, '', true); // true for isDirectory
      onRefresh();
      
      // Auto-start rename on new folder
      setTimeout(() => startRename({ file_path: fullPath, file_name: folderName } as UserFile), 100);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create folder",
        variant: "destructive",
      });
    }
  };

  const handleDragStart = (e: React.DragEvent, item: UserFile) => {
    setDraggedItem(item);
    e.dataTransfer.setData('text/plain', item.file_path);
  };

  const handleDragOver = (e: React.DragEvent, item: FileTreeItem) => {
    if (item.is_directory && draggedItem?.file_path !== item.file_path) {
      e.preventDefault();
      setDropTarget(item.file_path);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e: React.DragEvent, targetItem: FileTreeItem) => {
    e.preventDefault();
    setDropTarget(null);

    if (!draggedItem || !targetItem.is_directory || draggedItem.file_path === targetItem.file_path) {
      return;
    }

    try {
      const newPath = `${targetItem.file_path}\\${draggedItem.file_name}`;
      
      // Create new file/directory in new location
      await createFile(newPath, draggedItem.content || '', draggedItem.is_directory, draggedItem.content_type);
      
      // Delete old file/directory
      await deleteFile(draggedItem.file_path);
      
      toast({
        title: "Moved",
        description: `${draggedItem.file_name} moved to ${targetItem.file_name}`,
      });
      
      onRefresh();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to move item",
        variant: "destructive",
      });
    }
    
    setDraggedItem(null);
  };

  const renderFileTree = (items: FileTreeItem[]) => {
    return items.map(item => {
      const Icon = getFileIcon(item.file_name, item.is_directory);
      const isSelected = selectedItem === item.file_path;
      const isEditing = editingItem === item.file_path;
      const isDropping = dropTarget === item.file_path;

      return (
        <div key={item.file_path}>
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div
                className={`flex items-center gap-1 py-1 px-2 cursor-pointer hover:bg-accent/50 ${
                  isSelected ? 'bg-accent' : ''
                } ${isDropping ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
                style={{ paddingLeft: `${8 + item.level * 16}px` }}
                onClick={() => handleItemClick(item)}
                onDoubleClick={() => handleItemDoubleClick(item)}
                draggable={!isEditing}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={(e) => handleDragOver(e, item)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item)}
              >
                {item.is_directory && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-4 h-4 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFolder(item.file_path);
                    }}
                  >
                    {item.isExpanded ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                  </Button>
                )}
                
                {!item.is_directory && (
                  <div className="w-4" />
                )}
                
                <Icon className={`w-4 h-4 flex-shrink-0 ${
                  item.is_directory 
                    ? item.isExpanded 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-blue-500'
                    : 'text-muted-foreground'
                }`} />
                
                {isEditing ? (
                  <Input
                    ref={editInputRef}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename();
                      if (e.key === 'Escape') cancelRename();
                    }}
                    className="h-6 text-sm flex-1"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="text-sm truncate flex-1">
                    {item.file_name}
                  </span>
                )}
              </div>
            </ContextMenuTrigger>
            
            <ContextMenuContent>
              {!item.is_directory && (
                <>
                  <ContextMenuItem onClick={() => onFileSelect(item)}>
                    <FileText className="w-4 h-4 mr-2" />
                    Open
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                </>
              )}
              
              {item.is_directory && (
                <>
                  <ContextMenuItem onClick={() => createNewFile(item.file_path)}>
                    <File className="w-4 h-4 mr-2" />
                    New File
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => createNewFolder(item.file_path)}>
                    <Folder className="w-4 h-4 mr-2" />
                    New Folder
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                </>
              )}
              
              <ContextMenuItem onClick={() => startRename(item)}>
                <Edit3 className="w-4 h-4 mr-2" />
                Rename
              </ContextMenuItem>
              
              <ContextMenuItem onClick={() => {
                setItemToDelete(item);
                setShowDeleteDialog(true);
              }}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
          
          {item.is_directory && item.isExpanded && item.children && (
            <div>
              {renderFileTree(item.children)}
            </div>
          )}
        </div>
      );
    });
  };

  // Memoize allFiles and fileTree to prevent constant VFS calls on re-renders
  // These must be called before any conditional returns to follow Rules of Hooks
  const allFiles = useMemo(() => {
    if (!currentProject) return [];
    const base = currentProject.path.replace(/\//g, '\\');
    // Pull the complete list from VFS and filter to this project so recursion has all children
    return files.filter(f => f.file_path.startsWith(base));
  }, [files, currentProject?.path]);
  
  const fileTree = useMemo(() => {
    return currentProject ? buildFileTree(allFiles, currentProject.path) : [];
  }, [buildFileTree, allFiles, currentProject?.path]);

  if (!currentProject) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Folder className="w-12 h-12 mb-4 opacity-50" />
        <p className="text-sm text-center">
          Open a project to explore files
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-border p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Explorer</div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              className="w-6 h-6 p-0"
              onClick={() => createNewFile(currentPath)}
              title="New File"
            >
              <File className="w-3 h-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="w-6 h-6 p-0"
              onClick={() => createNewFolder(currentPath)}
              title="New Folder"
            >
              <Folder className="w-3 h-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="w-6 h-6 p-0"
              onClick={onRefresh}
              title="Refresh"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground font-mono bg-muted p-2 rounded">
          {currentProject.name}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-1">
          {fileTree.length > 0 ? (
            renderFileTree(fileTree)
          ) : (
            <div className="text-center text-muted-foreground text-sm p-4">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No files in this project</p>
            </div>
          )}
        </div>
      </ScrollArea>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {itemToDelete?.file_name}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.file_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FileTreeExplorer;